import Reveal from "@/components/feature/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    处理中: "bg-primary-100 text-primary-900",
    已回应: "bg-secondary-100 text-secondary-900",
    已处分: "bg-accent-100 text-accent-900",
  };
  return (
    <span className={`rounded-full px-2.5 py-1 text-[11px] ${styles[status] ?? "bg-background-200 text-foreground-700"}`}>
      {status}
    </span>
  );
}

export default function FeaturedEvents() {
  return (
    <section id="focus" className="w-full bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-8 md:py-24">
        <Reveal>
          <SectionHeading
            index="02"
            eyebrow="Focus"
            title="焦点事件：三起被反复讨论的校园事件"
            desc="每一起都同时保留了同学爆料、学校回应与处理进度。阅读顺序建议从事件经过开始，再看各方的说法，最后对照处分结果。"
          />
        </Reveal>

        <div className="mt-10 grid gap-8 md:mt-14 lg:grid-cols-3 lg:gap-8">
          <Reveal className="lg:col-span-2">
            <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-foreground-900/10 bg-background-100">
              <div className="h-[240px] w-full overflow-hidden md:h-[360px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20quiet%20dormitory%20room%20at%20night%2C%20warm%20cream%20paper%20tones%20with%20amber%20lamp%20glow%20and%20terracotta%20accents%2C%20neatly%20arranged%20desks%20and%20bunk%20beds%2C%20muted%20grainy%20magazine%20texture%2C%20no%20text%2C%20no%20faces%2C%20calm%20narrative%20mood%2C%20flat%20artistic%20composition%2C%20consistent%20warm%20palette&width=1000&height=640&seq=campus-file-focus-dorm-01&orientation=landscape"
                  alt="宿舍矛盾事件配图，黑料学生焦点事件"
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <div className="flex flex-1 flex-col p-6 md:p-8">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full border border-accent-200 bg-accent-50 px-3 py-1 text-xs text-accent-900">
                    宿舍矛盾
                  </span>
                  <StatusBadge status="处理中" />
                  <span className="text-xs text-foreground-500">2026-09-09</span>
                </div>
                <h4 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                  <a href="#focus" className="cursor-pointer transition-colors hover:text-accent-600">
                    熄灯后的争执：某高校宿舍矛盾升级，校方已介入沟通
                  </a>
                </h4>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">
                  同学提供的班级群聊显示，争执起因于作息时间分歧，随后在宿舍群内升级。宿管当晚到场协调，
                  学校次日回应称已安排心理疏导，并临时调整床位安排，最终处理意见仍在流程中。
                </p>
                <div className="mt-6 flex items-center justify-between border-t border-foreground-900/10 pt-4 text-xs text-foreground-500">
                  <span>同学爆料 · 校方回应</span>
                  <span className="flex items-center gap-1 text-foreground-700">
                    阅读整理
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </span>
                </div>
              </div>
            </article>
          </Reveal>

          <div className="flex flex-col gap-8">
            <Reveal delay={80}>
              <article className="group flex flex-col overflow-hidden rounded-lg border border-foreground-900/10 bg-background-100">
                <div className="h-[160px] w-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20campus%20notice%20board%20covered%20with%20blank%20paper%20notes%20and%20pinned%20letters%2C%20warm%20cream%20paper%20tones%20with%20amber%20and%20terracotta%20accents%2C%20soft%20light%2C%20muted%20grainy%20magazine%20texture%2C%20no%20readable%20text%2C%20no%20faces%2C%20flat%20artistic%20composition%2C%20consistent%20warm%20palette&width=800&height=520&seq=campus-file-focus-wall-01&orientation=landscape"
                    alt="表白墙风波事件配图，黑料学生同学爆料"
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5 md:p-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-accent-200 bg-accent-50 px-2.5 py-1 text-[11px] text-accent-900">
                      表白墙风波
                    </span>
                    <StatusBadge status="已回应" />
                  </div>
                  <h4 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
                    <a href="#focus" className="cursor-pointer transition-colors hover:text-accent-600">
                      匿名表白墙点名风波，当事人与同学各执一词
                    </a>
                  </h4>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    校方说明部分截图存在拼接，要求运营者删除相关内容，事情已进入沟通阶段。
                  </p>
                </div>
              </article>
            </Reveal>

            <Reveal delay={140}>
              <article className="group flex flex-col overflow-hidden rounded-lg border border-foreground-900/10 bg-background-100">
                <div className="h-[160px] w-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20an%20empty%20examination%20classroom%20with%20orderly%20desks%20and%20a%20large%20chalkboard%2C%20warm%20cream%20paper%20tones%20with%20amber%20and%20terracotta%20accents%2C%20soft%20window%20light%2C%20muted%20grainy%20magazine%20texture%2C%20no%20readable%20text%2C%20no%20faces%2C%20flat%20artistic%20composition%2C%20consistent%20warm%20palette&width=800&height=520&seq=campus-file-focus-exam-01&orientation=landscape"
                    alt="考试争议事件配图，黑料学生处分结果"
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5 md:p-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-accent-200 bg-accent-50 px-2.5 py-1 text-[11px] text-accent-900">
                      考试争议
                    </span>
                    <StatusBadge status="已处分" />
                  </div>
                  <h4 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
                    <a href="#focus" className="cursor-pointer transition-colors hover:text-accent-600">
                      期末监考争议持续发酵，教务处发布情况说明
                    </a>
                  </h4>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    违规监考人员被通报批评，成绩复核流程已完成，处理结果已向班级公示。
                  </p>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}